import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQuery } from "@/graphql/base-query";
import {
  GET_TARIFF_ENGINEERING_CANDIDATES_QUERY,
  GET_TARIFF_ENGINEERING_CANDIDATE_QUERY,
  GET_TARIFF_ENGINEERING_ANALYSIS_HISTORY_QUERY,
  GET_TARIFF_ENGINEERING_PRODUCTS_QUERY,
  GET_TARIFF_ENGINEERING_PRODUCT_QUERY,
  RUN_TARIFF_ENGINEERING_RESEARCH_MUTATION,
  RUN_TARIFF_ENGINEERING_RESEARCH_FOR_PRODUCT_MUTATION,
  LINK_CHAT_TO_PRODUCT_MUTATION,
  UNLINK_CHAT_FROM_PRODUCT_MUTATION,
  GET_CHATS_FOR_PICKER_QUERY,
  GET_PRODUCT_ATTACHMENTS_QUERY,
  GENERATE_PRODUCT_ATTACHMENT_UPLOAD_URL_MUTATION,
  CREATE_PRODUCT_ATTACHMENT_MUTATION,
  DELETE_PRODUCT_ATTACHMENT_MUTATION,
} from "@/graphql/tariff-engineering-requests";
import {
  TariffEngineeringCandidate,
  TariffEngineeringCandidateResponse,
  TariffEngineeringCandidatesResponse,
  TariffEngineeringAnalysisHistoryResponse,
  RunTariffEngineeringResearchResponse,
  RunTariffEngineeringResearchForProductResponse,
  GetTariffEngineeringCandidatesArgs,
  GetTariffEngineeringProductsArgs,
  TariffEngineeringAnalysis,
  TariffEngineeringProduct,
  TariffEngineeringProductsResponse,
  TariffEngineeringProductResponse,
  ChatPickerRow,
  ChatsForPickerResponse,
  ProductAttachment,
  ProductAttachmentsResponse,
  GenerateProductAttachmentUploadUrlResponse,
  CreateProductAttachmentResponse,
  DeleteProductAttachmentResponse,
} from "@/types/tariff-engineering";

type GetCandidateArgs = {
  htsCode: string;
  countryOfOrigin: string;
  tenantId?: string;
  clientId?: string;
};

// History can be scoped to a product (per-product analyses) or left
// product-less for the trade-data pair's history. Kept separate so the
// candidate queries above don't gain an irrelevant productId.
type GetAnalysisHistoryArgs = GetCandidateArgs & {
  productId?: string;
};

type RunArgs = {
  htsCode: string;
  countryOfOrigin: string;
  tenantId?: string;
  clientId?: string;
};

const pairTag = (htsCode: string, countryOfOrigin: string) =>
  `${htsCode}|${countryOfOrigin}`;

export const tariffEngineeringApi = createApi({
  reducerPath: "api/tariff-engineering",
  baseQuery,
  tagTypes: [
    "TariffEngCandidate",
    "TariffEngCandidateList",
    "TariffEngAnalysisHistory",
    "TariffEngProduct",
    "TariffEngProductList",
    "ProductAttachments",
  ],
  endpoints: (builder) => ({
    getTariffEngineeringCandidates: builder.query<
      TariffEngineeringCandidatesResponse["tariffEngineeringCandidates"],
      GetTariffEngineeringCandidatesArgs | void
    >({
      query: (args = {}) => ({
        document: GET_TARIFF_ENGINEERING_CANDIDATES_QUERY,
        variables: args || {},
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((c) => ({
                type: "TariffEngCandidate" as const,
                id: pairTag(c.htsCode, c.countryOfOrigin),
              })),
              { type: "TariffEngCandidateList", id: "LIST" },
            ]
          : [{ type: "TariffEngCandidateList", id: "LIST" }],
      transformResponse: (response: TariffEngineeringCandidatesResponse) =>
        response.tariffEngineeringCandidates,
    }),

    getTariffEngineeringCandidate: builder.query<
      TariffEngineeringCandidate | null,
      GetCandidateArgs
    >({
      query: (args) => ({
        document: GET_TARIFF_ENGINEERING_CANDIDATE_QUERY,
        variables: args,
      }),
      providesTags: (_result, _error, args) => [
        {
          type: "TariffEngCandidate" as const,
          id: pairTag(args.htsCode, args.countryOfOrigin),
        },
      ],
      transformResponse: (response: TariffEngineeringCandidateResponse) =>
        response.tariffEngineeringCandidate,
    }),

    getTariffEngineeringAnalysisHistory: builder.query<
      TariffEngineeringAnalysis[],
      GetAnalysisHistoryArgs
    >({
      query: (args) => ({
        document: GET_TARIFF_ENGINEERING_ANALYSIS_HISTORY_QUERY,
        variables: args,
      }),
      providesTags: (_result, _error, args) => [
        {
          type: "TariffEngAnalysisHistory" as const,
          id: `${pairTag(args.htsCode, args.countryOfOrigin)}${args.productId ? `|${args.productId}` : ""}`,
        },
      ],
      transformResponse: (response: TariffEngineeringAnalysisHistoryResponse) =>
        response.tariffEngineeringAnalysisHistory,
    }),

    runTariffEngineeringResearch: builder.mutation<
      TariffEngineeringAnalysis,
      RunArgs
    >({
      query: (args) => ({
        document: RUN_TARIFF_ENGINEERING_RESEARCH_MUTATION,
        variables: args,
      }),
      invalidatesTags: (_result, _error, args) => [
        {
          type: "TariffEngCandidate",
          id: pairTag(args.htsCode, args.countryOfOrigin),
        },
        { type: "TariffEngCandidateList", id: "LIST" },
        {
          type: "TariffEngAnalysisHistory",
          id: pairTag(args.htsCode, args.countryOfOrigin),
        },
      ],
      transformResponse: (response: RunTariffEngineeringResearchResponse) =>
        response.runTariffEngineeringResearch,
    }),

    getTariffEngineeringProducts: builder.query<
      TariffEngineeringProductsResponse["tariffEngineeringProducts"],
      GetTariffEngineeringProductsArgs | void
    >({
      query: (args = {}) => ({
        document: GET_TARIFF_ENGINEERING_PRODUCTS_QUERY,
        variables: args || {},
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((p) => ({
                type: "TariffEngProduct" as const,
                id: p._id,
              })),
              { type: "TariffEngProductList", id: "LIST" },
            ]
          : [{ type: "TariffEngProductList", id: "LIST" }],
      transformResponse: (response: TariffEngineeringProductsResponse) =>
        response.tariffEngineeringProducts,
    }),

    getTariffEngineeringProduct: builder.query<
      TariffEngineeringProduct | null,
      { productId: string; tenantId?: string; clientId?: string }
    >({
      query: (args) => ({
        document: GET_TARIFF_ENGINEERING_PRODUCT_QUERY,
        variables: args,
      }),
      providesTags: (_result, _error, args) => [
        { type: "TariffEngProduct" as const, id: args.productId },
      ],
      transformResponse: (response: TariffEngineeringProductResponse) =>
        response.tariffEngineeringProduct,
    }),

    linkChatToProduct: builder.mutation<
      Partial<TariffEngineeringProduct>,
      {
        productId: string;
        chatId: string;
        tenantId?: string;
        clientId?: string;
      }
    >({
      query: (args) => ({
        document: LINK_CHAT_TO_PRODUCT_MUTATION,
        variables: args,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "TariffEngProduct", id: args.productId },
      ],
      transformResponse: (response: any) => response.linkChatToProduct,
    }),

    unlinkChatFromProduct: builder.mutation<
      Partial<TariffEngineeringProduct>,
      {
        productId: string;
        chatId: string;
        tenantId?: string;
        clientId?: string;
      }
    >({
      query: (args) => ({
        document: UNLINK_CHAT_FROM_PRODUCT_MUTATION,
        variables: args,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "TariffEngProduct", id: args.productId },
      ],
      transformResponse: (response: any) => response.unlinkChatFromProduct,
    }),

    getChatsForPicker: builder.query<
      ChatPickerRow[],
      { searchKeyword?: string; pageSize?: number } | void
    >({
      query: (args) => ({
        document: GET_CHATS_FOR_PICKER_QUERY,
        variables: { filter: args || {} },
      }),
      transformResponse: (response: ChatsForPickerResponse) =>
        response.chats?.data || [],
    }),

    getProductAttachments: builder.query<
      ProductAttachment[],
      { productId: string; tenantId?: string; clientId?: string }
    >({
      query: (args) => ({
        document: GET_PRODUCT_ATTACHMENTS_QUERY,
        variables: args,
      }),
      providesTags: (_result, _error, args) => [
        { type: "ProductAttachments" as const, id: args.productId },
      ],
      transformResponse: (response: ProductAttachmentsResponse) =>
        response.productAttachments,
    }),

    generateProductAttachmentUploadUrl: builder.mutation<
      { signedUrl: string; s3Key: string },
      {
        productId: string;
        filename: string;
        mimeType: string;
        sizeBytes: number;
        tenantId?: string;
        clientId?: string;
      }
    >({
      query: (args) => ({
        document: GENERATE_PRODUCT_ATTACHMENT_UPLOAD_URL_MUTATION,
        variables: args,
      }),
      transformResponse: (
        response: GenerateProductAttachmentUploadUrlResponse
      ) => response.generateProductAttachmentUploadUrl,
    }),

    createProductAttachment: builder.mutation<
      ProductAttachment,
      {
        productId: string;
        s3Key: string;
        filename: string;
        mimeType: string;
        sizeBytes: number;
        tenantId?: string;
        clientId?: string;
      }
    >({
      query: (args) => ({
        document: CREATE_PRODUCT_ATTACHMENT_MUTATION,
        variables: args,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "ProductAttachments", id: args.productId },
      ],
      transformResponse: (response: CreateProductAttachmentResponse) =>
        response.createProductAttachment,
    }),

    deleteProductAttachment: builder.mutation<
      boolean,
      {
        attachmentId: string;
        productId: string;
        tenantId?: string;
        clientId?: string;
      }
    >({
      query: ({ attachmentId, tenantId, clientId }) => ({
        document: DELETE_PRODUCT_ATTACHMENT_MUTATION,
        variables: { attachmentId, tenantId, clientId },
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "ProductAttachments", id: args.productId },
      ],
      transformResponse: (response: DeleteProductAttachmentResponse) =>
        response.deleteProductAttachment,
    }),

    runTariffEngineeringResearchForProduct: builder.mutation<
      TariffEngineeringAnalysis,
      { productId: string; tenantId?: string; clientId?: string }
    >({
      query: (args) => ({
        document: RUN_TARIFF_ENGINEERING_RESEARCH_FOR_PRODUCT_MUTATION,
        variables: args,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "TariffEngProduct", id: args.productId },
        { type: "TariffEngProductList", id: "LIST" },
      ],
      transformResponse: (
        response: RunTariffEngineeringResearchForProductResponse
      ) => response.runTariffEngineeringResearchForProduct,
    }),
  }),
});

export const {
  useGetTariffEngineeringCandidatesQuery,
  useGetTariffEngineeringCandidateQuery,
  useGetTariffEngineeringAnalysisHistoryQuery,
  useRunTariffEngineeringResearchMutation,
  useGetTariffEngineeringProductsQuery,
  useGetTariffEngineeringProductQuery,
  useRunTariffEngineeringResearchForProductMutation,
  useLinkChatToProductMutation,
  useUnlinkChatFromProductMutation,
  useGetChatsForPickerQuery,
  useGetProductAttachmentsQuery,
  useGenerateProductAttachmentUploadUrlMutation,
  useCreateProductAttachmentMutation,
  useDeleteProductAttachmentMutation,
} = tariffEngineeringApi;
